import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Calendar, ArrowRight } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';
import { formatDate as formatLocalizedDate } from '@/lib/format-date';
import type { NewsArticle } from '@/types';

/** Convert a plain-text paragraph into React nodes, turning http(s) URLs into clickable links. */
function renderWithLinks(text: string): React.ReactNode {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (!part) return null;
    if (urlRegex.test(part)) {
      // Strip trailing punctuation from URL so it isn't broken by a period/comma at sentence end
      const trailingMatch = part.match(/[.,!?;:)\]]+$/);
      const trailing = trailingMatch ? trailingMatch[0] : '';
      const url = trailing ? part.slice(0, -trailing.length) : part;
      return (
        <React.Fragment key={i}>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-black/30 dark:decoration-white/30 hover:decoration-black dark:hover:decoration-white text-black dark:text-white break-all"
          >
            {url}
          </a>
          {trailing}
        </React.Fragment>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

const NewsPage: React.FC = () => {
  const { t, language, localize } = useTranslation();
  const settings = useAdminStore((s) => s.settings);
  const allArticles = useAdminStore((s) => s.newsArticles);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const articles = useMemo(
    () =>
      allArticles
        .filter((a) => a.isPublished)
        .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()),
    [allArticles],
  );

  const selected = selectedId ? articles.find((a) => a._id === selectedId) : null;
  const featured = articles[0];
  const rest = articles.slice(1);

  const formatDate = (date: string): string => formatLocalizedDate(date, language);

  if (selected) {
    return <ArticleView article={selected} onBack={() => setSelectedId(null)} localize={localize} formatDate={formatDate} t={t} />;
  }

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-20 px-4 sm:px-6 md:px-24">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-10 sm:mb-16"
        >
          <h1 className="text-3xl sm:text-5xl md:text-8xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tighter leading-none">
            {t.news.title}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-black/40 dark:text-white/40 font-light">
            {t.news.subtitle}
          </p>
        </motion.div>

        {articles.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">
              {t.news.noArticles}
            </p>
          </div>
        ) : (
          <>
            {/* Featured Article */}
            {featured && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="mb-12 sm:mb-20"
              >
                <button
                  onClick={() => setSelectedId(featured._id)}
                  className="w-full text-left group"
                >
                  <div className="relative aspect-[21/9] rounded-2xl sm:rounded-3xl overflow-hidden bg-[#f0f0f0] dark:bg-[#0a0a0a] border border-black/5 dark:border-white/5">
                    <img
                      src={featured.coverImage}
                      alt={localize(featured.title)}
                      className="w-full h-full object-cover grayscale-[0.3] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    {/* Content overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10 md:p-14">
                      <span className="inline-block px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm text-[8px] sm:text-[9px] font-bold uppercase tracking-[0.2em] text-white/80 mb-3 sm:mb-4">
                        {t.news.featured}
                      </span>
                      <h2 className="text-xl sm:text-3xl md:text-5xl font-syncopate font-bold text-white uppercase tracking-tighter leading-none mb-2 sm:mb-4">
                        {localize(featured.title)}
                      </h2>
                      <p className="text-sm sm:text-base text-white/60 font-light max-w-2xl leading-relaxed hidden sm:block whitespace-pre-line text-justify">
                        {localize(featured.excerpt)}
                      </p>
                      <div className="flex items-center gap-4 mt-3 sm:mt-5">
                        <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.15em] text-white/40 flex items-center gap-1.5">
                          <Calendar size={12} />
                          {formatDate(featured.publishedAt)}
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/60 flex items-center gap-1.5 group-hover:text-white transition-colors">
                          {t.news.readMore} <ArrowRight size={12} />
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              </motion.div>
            )}

            {/* Article Grid */}
            {rest.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {rest.map((article, i) => (
                  <motion.button
                    key={article._id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + i * 0.1, duration: 0.6 }}
                    onClick={() => setSelectedId(article._id)}
                    className="text-left group"
                  >
                    <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-[#f0f0f0] dark:bg-[#0a0a0a] border border-black/5 dark:border-white/5 mb-4">
                      <img
                        src={article.coverImage}
                        alt={localize(article.title)}
                        className="w-full h-full object-cover grayscale-[0.3] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                      />
                    </div>
                    <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-black/25 dark:text-white/25 flex items-center gap-1.5 mb-2">
                      <Calendar size={10} />
                      {formatDate(article.publishedAt)}
                    </span>
                    <h3 className="text-base sm:text-lg font-syncopate font-bold text-black dark:text-white uppercase tracking-tight leading-tight mb-2 group-hover:opacity-70 transition-opacity">
                      {localize(article.title)}
                    </h3>
                    <p className="text-xs sm:text-sm text-black/40 dark:text-white/40 font-light leading-relaxed line-clamp-2">
                      {localize(article.excerpt)}
                    </p>
                  </motion.button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/* ── Article Full View ── */
interface ArticleViewProps {
  article: NewsArticle;
  onBack: () => void;
  localize: (text: { ka: string; en: string }) => string;
  formatDate: (date: string) => string;
  t: ReturnType<typeof import('@/hooks/useTranslation').useTranslation>['t'];
}

const ArticleView: React.FC<ArticleViewProps> = ({ article, onBack, localize, formatDate, t }) => {
  const contentLines = localize(article.content).split('\n').filter(Boolean);

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-20 px-4 sm:px-6 md:px-24">
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <motion.button
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
          onClick={onBack}
          className="flex items-center gap-2 mb-8 text-[10px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 hover:text-black dark:hover:text-white transition-colors"
        >
          <ArrowLeft size={14} />
          {t.news.backToNews}
        </motion.button>

        {/* Cover */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="aspect-[21/9] rounded-2xl sm:rounded-3xl overflow-hidden bg-[#f0f0f0] dark:bg-[#0a0a0a] border border-black/5 dark:border-white/5 mb-8 sm:mb-12"
        >
          <img
            src={article.coverImage}
            alt={localize(article.title)}
            className="w-full h-full object-cover"
          />
        </motion.div>

        {/* Meta */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-6"
        >
          <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/25 dark:text-white/25 flex items-center gap-1.5 mb-4">
            <Calendar size={11} />
            {t.news.publishedOn} {formatDate(article.publishedAt)}
          </span>
          <h1 className="text-2xl sm:text-4xl md:text-6xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tighter leading-none">
            {localize(article.title)}
          </h1>
        </motion.div>

        {/* Excerpt */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="text-base sm:text-lg text-black/50 dark:text-white/50 font-light leading-relaxed mb-10 pb-8 border-b border-black/5 dark:border-white/5 whitespace-pre-line text-justify"
        >
          {localize(article.excerpt)}
        </motion.p>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="space-y-5"
        >
          {contentLines.map((paragraph, i) => (
            <p key={i} className="text-sm sm:text-[15px] text-black/60 dark:text-white/60 font-light leading-[1.9]">
              {renderWithLinks(paragraph)}
            </p>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default NewsPage;
